// The Calendar (`/c/{slug}/calendar`; CALENDAR-C1, docs/design/2026-10-28-calendar.md §3): the
// calendars the person follows and shows, overlaid, in a day, week, month or agenda view (the
// view and the day in the search), in the person's browser zone. Quick add, edit, details and
// the recurring scope question are modals; the page never changes under them. The "Tasks"
// overlay (the sidebar's switch) adds the tasks due as read-only chips at the top of each day.
import {
  Button,
  LocationHeader,
  Notice,
  Segmented,
  type TagChipColor,
  toast,
  useIsMobile,
} from "@companyos/ui";
import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  PlusIcon,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import type { Api } from "../../api/client";
import type {
  CalendarEventDetail,
  CalendarItem,
  CalendarOccurrence,
  CalendarTaskChip,
  CalendarView,
} from "../../api/types";
import type { CompanyAccess } from "../company";
import { LoadError } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { dayWrite } from "../work/card-fields";
import { FindTimeDialog } from "./calendar-dialogs";
import {
  type CalendarViewKind,
  daysOf,
  type NewTimes,
  colorOf as paletteOf,
  stepAnchor,
  viewTitle,
  windowOf,
  ymd,
} from "./calendar-model";
import {
  calendarItemsQuery,
  calendarKeys,
  calendarsQuery,
  calendarTasksQuery,
  eventQuery,
  occurrencesQuery,
  selectionOf,
} from "./calendar-queries";
import { useTasksOverlay } from "./calendar-toggles";
import { AgendaList, type CalendarActions, MonthGrid, TimeGrid } from "./calendar-views";
import {
  ConfirmDelete,
  EventDetailDialog,
  EventFormDialog,
  type EventFormSeed,
  ScopeDialog,
  slotOf,
  useEventWrites,
} from "./event-dialogs";

type Search = {
  view?: CalendarViewKind | undefined;
  date?: string | undefined;
  event?: string | undefined;
  at?: string | undefined;
};

type Modal =
  | { kind: "detail"; o: CalendarOccurrence }
  | { kind: "form"; seed: EventFormSeed }
  | { kind: "delete"; o: CalendarOccurrence }
  | { kind: "move"; o: CalendarOccurrence; times: NewTimes }
  | { kind: "find" };

/** An occurrence for an event opened by link (the board's chips, a copied link). */
function occurrenceOf(d: CalendarEventDetail): CalendarOccurrence {
  return {
    key: `${d.id}:${d.occurrence_start ?? d.starts_at}`,
    // The row read: an exception keeps its own id (never its series'), a series' occurrence is
    // the master's id with its slot.
    event_id: d.id,
    series_id: d.series_id,
    calendar_id: d.calendar_id,
    starts_at: d.starts_at,
    ends_at: d.ends_at,
    all_day: d.all_day,
    timezone: d.timezone,
    original_start_at: d.occurrence_start,
    recurring: Boolean(d.series_id || d.rrule || d.series_rrule),
    busy: false,
    title: d.title,
    location: d.location,
    status: d.status,
    source: d.source,
    source_ref: d.source_ref,
    organizer_membership_id: d.organizer_membership_id,
    organizer_name: d.organizer_name,
    owner_name: null,
    attendee_count: d.attendees.length,
    my_response: d.my_response,
    attending: d.my_response !== null,
    can_edit: d.can_edit,
    can_rsvp: d.can_rsvp,
  };
}

export function CalendarScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as Search;
  const mobile = useIsMobile();
  const today = ymd(new Date());
  const view: CalendarViewKind = search.view ?? (mobile ? "day" : "week");
  const anchor = search.date ?? today;
  const [modal, setModal] = useState<Modal | null>(null);
  const [tasksOn] = useTasksOverlay(api, co);
  const writes = useEventWrites(api, co);

  const cals = useQuery(calendarsQuery(api, co));
  const weekStart = cals.data?.week_start ?? 1;
  const days = useMemo(() => daysOf(view, anchor, weekStart), [view, anchor, weekStart]);
  const win = windowOf(days);
  // The window is read per selection of calendars, so a follow or a show/hide never leaves an
  // older selection's answer standing in for the current one.
  const selection = selectionOf(cals.data?.data ?? []);
  const occ = useQuery({
    ...occurrencesQuery(api, co, win.from, win.to, selection),
    enabled: cals.isSuccess,
  });
  const selecting = useIsMutating({ mutationKey: calendarKeys.selection(co) }) > 0;
  // Find a time suggests only from a complete read of the current selection: not while the
  // calendars or the window refetch, nor while a follow or show/hide is in flight.
  const findStatus: "pending" | "error" | "success" =
    cals.isError || occ.isError
      ? "error"
      : occ.isSuccess && !occ.isFetching && !cals.isFetching && !selecting
        ? "success"
        : "pending";
  const tasks = useQuery({
    ...calendarTasksQuery(api, co, win.from, win.to),
    enabled: tasksOn,
    retry: false,
  });
  // CAL-WORK-LAYERS: the shown work calendars' and Milestones' chips (never busy).
  const layered = (cals.data?.data ?? []).some(
    (c) => (c.kind === "work" || c.kind === "milestones") && c.shown,
  );
  const items = useQuery({
    ...calendarItemsQuery(api, co, win.from, win.to, selection),
    enabled: cals.isSuccess && layered,
    retry: false,
  });
  const moveItem = useMutation({
    mutationFn: async ({ i, day }: { i: CalendarItem; day: string }) => {
      const w = dayWrite(i.date_field ?? "due_at", day);
      if (w.kind === "field")
        await api.client.tasks.setField({
          params: { co, id: i.ref_id, field_id: w.field },
          body: { value: w.value },
        });
      else
        await api.client.tasks.update({
          params: { co, id: i.ref_id },
          ifMatch: i.version ?? 0,
          body: w.body,
        });
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: calendarKeys.root(co) });
      void qc.invalidateQueries({ queryKey: ["company", co, "work"] });
    },
    onError: (e) =>
      toast.add({ title: problemText(e, "The date could not be moved."), type: "error" }),
  });

  const byId = useMemo(
    () => new Map((cals.data?.data ?? []).map((c) => [c.id, c] as const)),
    [cals.data],
  );
  const colorOf = (id: string): TagChipColor => paletteOf(id, byId);
  // A hidden calendar's events go at once (the refetch confirms); what the person attends stays.
  const occurrences = (occ.data?.data ?? []).filter((o) => {
    const c = byId.get(o.calendar_id);
    return !c || c.shown || o.attending;
  });
  const calendars: CalendarView[] = cals.data?.data ?? [];
  const truncated = occ.data?.truncated === true || items.data?.truncated === true;
  const writable = calendars.some((c) => c.access === "write");

  const setSearch = (patch: Partial<Search>) =>
    void navigate({
      to: "/c/$slug/calendar",
      params: { slug: company.slug },
      search: (prev: Record<string, unknown>) => {
        const next: Record<string, unknown> = { ...prev, ...patch };
        for (const k of Object.keys(patch)) if (next[k] === undefined) delete next[k];
        return next;
      },
      replace: true,
    } as never);

  const detailOf = (o: CalendarOccurrence) =>
    qc.fetchQuery(eventQuery(api, co, o.event_id, o.recurring || o.series_id ? slotOf(o) : null));

  const linkOf = (
    o: Pick<CalendarOccurrence, "event_id" | "starts_at" | "original_start_at" | "recurring">,
  ) => {
    const params = new URLSearchParams({ date: ymd(new Date(o.starts_at)), event: o.event_id });
    if (o.recurring) params.set("at", slotOf(o));
    return `${window.location.origin}/c/${company.slug}/calendar?${params}`;
  };

  const actions: CalendarActions = {
    open: (o) => {
      if (!o.busy) setModal({ kind: "detail", o });
    },
    edit: (o) => {
      detailOf(o).then(
        (detail) => setModal({ kind: "form", seed: { mode: "edit", occurrence: o, detail } }),
        (e) =>
          toast.add({ title: problemText(e, "The event could not be opened."), type: "error" }),
      );
    },
    duplicate: (o) => {
      detailOf(o).then(
        (detail) => setModal({ kind: "form", seed: { mode: "duplicate", detail } }),
        (e) =>
          toast.add({ title: problemText(e, "The event could not be opened."), type: "error" }),
      );
    },
    remove: (o) => setModal({ kind: "delete", o }),
    copyLink: (o) => {
      const url = linkOf(o);
      navigator.clipboard?.writeText(url).then(
        () => toast.add({ title: "Link copied", type: "success" }),
        () => toast.add({ title: "Could not copy the link", type: "error" }),
      );
    },
    slot: (day, hour) => {
      if (writable) setModal({ kind: "form", seed: { mode: "create", day, hour } });
    },
    task: (t: CalendarTaskChip) =>
      void navigate({
        to: "/c/$slug/work",
        params: { slug: company.slug },
        search: { task: t.id },
      } as never),
    item: (i: CalendarItem) =>
      // Shared with someone off the board (no board id): the chip is all they may see.
      i.kind === "task" && !i.board_id
        ? toast.add({ title: "Only people on the board can open this task.", type: "info" })
        : i.kind === "task"
          ? void navigate({
              to: "/c/$slug/work",
              params: { slug: company.slug },
              search: { task: i.ref_id },
            } as never)
          : void navigate({
              to: "/c/$slug/kpis/priorities",
              params: { slug: company.slug },
            } as never),
    moveItem: (i, day) => {
      if (i.kind === "task" && i.can_edit) moveItem.mutate({ i, day });
    },
    showDay: (day) => setSearch({ view: "day", date: day }),
    // A series asks This / Following / All first, as the Edit modal does.
    move: (o, times) => {
      if (o.recurring) setModal({ kind: "move", o, times });
      else writes.move.mutate({ o, times, scope: null });
    },
  };

  const closeLinked = () => setSearch({ event: undefined, at: undefined });
  const shared = {
    occurrences,
    tasks: tasksOn ? (tasks.data?.data ?? []) : null,
    // A hidden layer's chips go at once (the refetch confirms).
    items: layered
      ? (items.data?.data ?? []).filter((i) => byId.get(i.calendar_id)?.shown !== false)
      : [],
    colorOf,
    today,
    actions,
  };

  return (
    <div
      className="flex h-full min-h-0 min-w-0 flex-1 flex-col"
      data-slot="calendar"
      data-view={view}
    >
      <LocationHeader
        icon={CalendarDaysIcon}
        title="Calendar"
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => setModal({ kind: "find" })}>
              <ClockIcon data-icon="inline-start" aria-hidden="true" />
              <span className="max-sm:sr-only">Find a time</span>
            </Button>
            {writable ? (
              <Button
                size="sm"
                onClick={() =>
                  setModal({
                    kind: "form",
                    seed: { mode: "create", day: view === "month" ? today : anchor, hour: null },
                  })
                }
              >
                <PlusIcon data-icon="inline-start" aria-hidden="true" />
                <span className="max-sm:sr-only">New event</span>
              </Button>
            ) : null}
          </>
        }
      />
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-subtle px-6 py-2 max-md:px-4">
        <Button variant="secondary" size="sm" onClick={() => setSearch({ date: undefined })}>
          Today
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Previous"
          onClick={() => setSearch({ date: stepAnchor(view, anchor, -1) })}
        >
          <ChevronLeftIcon />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Next"
          onClick={() => setSearch({ date: stepAnchor(view, anchor, 1) })}
        >
          <ChevronRightIcon />
        </Button>
        <h2
          className="min-w-0 truncate text-md font-semibold tracking-heading text-foreground"
          data-slot="calendar-title"
        >
          {viewTitle(view, days, anchor)}
        </h2>
        <Segmented<CalendarViewKind>
          aria-label="View"
          size="sm"
          className="ml-auto"
          value={view}
          onValueChange={(v) => setSearch({ view: v })}
          options={[
            { value: "day", label: "Day" },
            { value: "week", label: "Week" },
            { value: "month", label: "Month" },
            { value: "agenda", label: "Agenda" },
          ]}
        />
      </div>
      {cals.isError ? (
        <LoadError what="The calendars" error={cals.error} />
      ) : occ.isError ? (
        <LoadError what="The events" error={occ.error} />
      ) : null}
      {truncated ? (
        <Notice
          tone="info"
          className="mx-6 mt-2 shrink-0 py-2 max-md:mx-4"
          data-slot="calendar-truncated"
        >
          Too many events here; some are not shown. Narrow the view or hide a calendar.
        </Notice>
      ) : null}
      {cals.isError || occ.isError ? null : view === "month" ? (
        <MonthGrid {...shared} days={days} anchor={anchor} />
      ) : view === "agenda" ? (
        <AgendaList {...shared} days={days} />
      ) : (
        <TimeGrid {...shared} days={days} companyZone={company.calendar.timezone} />
      )}

      {modal?.kind === "detail" ? (
        <EventDetailDialog
          api={api}
          company={company}
          occurrence={modal.o}
          color={colorOf(modal.o.calendar_id)}
          onClose={() => setModal(null)}
          onEdit={(detail) =>
            setModal({ kind: "form", seed: { mode: "edit", occurrence: modal.o, detail } })
          }
          onDuplicate={(detail) => setModal({ kind: "form", seed: { mode: "duplicate", detail } })}
          onDelete={() => setModal({ kind: "delete", o: modal.o })}
          onCopyLink={() => actions.copyLink(modal.o)}
        />
      ) : null}
      {search.event && !modal ? (
        <LinkedEvent
          api={api}
          company={company}
          id={search.event}
          at={search.at ?? null}
          onClose={closeLinked}
          onOpen={(o) => {
            closeLinked();
            setModal({ kind: "detail", o });
          }}
        />
      ) : null}
      {modal?.kind === "form" ? (
        <EventFormDialog
          api={api}
          company={company}
          calendars={calendars}
          myCalendarId={cals.data?.my_calendar_id ?? null}
          myMembershipId={cals.data?.my_membership_id ?? null}
          seed={modal.seed}
          onClose={() => setModal(null)}
        />
      ) : null}
      {modal?.kind === "delete" ? (
        modal.o.recurring ? (
          <ScopeDialog
            verb="Delete"
            onClose={() => setModal(null)}
            onPick={(scope) => {
              writes.remove.mutate({ o: modal.o, scope });
              setModal(null);
            }}
          />
        ) : (
          <ConfirmDelete
            title={modal.o.title ?? "this event"}
            onClose={() => setModal(null)}
            onConfirm={() => {
              writes.remove.mutate({ o: modal.o });
              setModal(null);
            }}
          />
        )
      ) : null}
      {modal?.kind === "move" ? (
        <ScopeDialog
          verb="Save"
          onClose={() => setModal(null)}
          onPick={(scope) => {
            writes.move.mutate({ o: modal.o, times: modal.times, scope });
            setModal(null);
          }}
        />
      ) : null}
      {modal?.kind === "find" ? (
        <FindTimeDialog
          days={view === "month" ? days.filter((d) => d >= today).slice(0, 14) : days}
          initialDay={days.includes(today) ? today : anchor}
          occurrences={occurrences}
          truncated={occ.data?.truncated === true}
          onClose={() => setModal(null)}
          status={findStatus}
          onPick={(day, hour, minute, length) =>
            setModal(
              writable
                ? { kind: "form", seed: { mode: "create", day, hour, minute, length } }
                : null,
            )
          }
        />
      ) : null}
    </div>
  );
}

/** The route's frame: the mode root fills the main area, so only the time grid scrolls. */
export function CalendarFrame({ children }: { children: ReactNode }) {
  return (
    <div className="h-full [&>[data-mode-root]]:flex [&>[data-mode-root]]:h-full [&>[data-mode-root]]:flex-col">
      {children}
    </div>
  );
}

/** An event opened by link: its details once read (the window may not hold it). */
function LinkedEvent({
  api,
  company,
  id,
  at,
  onOpen,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
  at: string | null;
  onOpen: (o: CalendarOccurrence) => void;
  onClose: () => void;
}) {
  const q = useQuery({ ...eventQuery(api, company.id, id, at), retry: false });
  const latest = useRef({ onOpen, onClose });
  latest.current = { onOpen, onClose };
  useEffect(() => {
    if (q.data) latest.current.onOpen(occurrenceOf(q.data));
    else if (q.isError) {
      toast.add({ title: problemText(q.error, "The event could not be opened."), type: "error" });
      latest.current.onClose();
    }
  }, [q.data, q.isError, q.error]);
  return null;
}
